import React from "react";
import { Helmet } from "react-helmet";
import TechIntro from "../ReusableComponents/TechIntro";
import TechWhyChoose from "../ReusableComponents/TechWhyChoose";
import TechRoles from "../ReusableComponents/TechRoles";
import TechStaffingServices from "../ReusableComponents/TechStaffingServices";
import TechRecruitingAgency from "../ReusableComponents/TechRecruitingAgency";
import TechOutcomes from "../ReusableComponents/TechOutcomes";
import TechCTA from "../ReusableComponents/TechCTA";

const roleGroups = [
  {
    title: 'Core Products',
    items: [
      { name: 'Epicor Kinetic (ERP 10)', icon: '⚙️' },
      { name: 'Epicor Prophet 21', icon: '📦' },
      { name: 'Epicor Eclipse', icon: '🌑' },
      { name: 'Epicor BisTrack', icon: '🪵' },
    ]
  },
  {
    title: 'Functional Modules',
    items: [
      { name: 'Epicor Manufacturing (MES)', icon: '🏭' },
      { name: 'Epicor Finance & Accounting', icon: '💰' },
      { name: 'Epicor Supply Chain / Procurement', icon: '🔗' },
      { name: 'Epicor HCM / Payroll', icon: '👥' },
    ]
  },
  {
    title: 'Technical & Cloud',
    items: [
      { name: 'Epicor Technical Developer', icon: '💻' },
      { name: 'Epicor BAQ / BPM Engineer', icon: '🔧' },
      { name: 'Epicor Cloud / SaaS Architect', icon: '☁️' },
      { name: 'Epicor CRM', icon: '📋' },
    ]
  }
];

const features = [
  {
    icon: '✦',
    title: 'Deep Epicor Product Knowledge',
    text: 'Our recruiters understand Epicor\'s product family — Kinetic architecture, BPM and BAQ tools, Prophet 21 distribution workflows, MES configuration, and ERP 10 upgrade paths. We assess real Epicor competency, not generic ERP experience.',
  },
  {
    icon: '✔',
    title: 'Rigorous Epicor-Specific Vetting',
    text: 'All Epicor consultants are verified for: Epicor Kinetic or product-specific implementation experience, technical skills (BAQ, BPM, SSRS, REST API, Epicor customization), manufacturing or distribution domain knowledge, and cloud vs. on-premise deployment expertise.',
  },
  {
    icon: '⚙',
    title: 'Flexible Engagement Models',
    text: 'Contract, contract-to-hire, or full-time. From an individual Epicor consultant to a full delivery team — we structure engagements to fit your project timeline, scope, and budget.',
  },
  {
    icon: '🛡',
    title: 'Epicor Cloud & Kinetic Migration Expertise',
    text: 'We source consultants experienced in ERP 10 to Epicor Kinetic SaaS migrations — ensuring a smooth, well-governed path to the cloud with minimal operational disruption.',
  },
];

const problemItems = [
  'ERP go-live failures from consultants unfamiliar with Epicor Kinetic\'s event-based architecture or BPM framework.',
  'Manufacturing workflow errors due to lack of experience with Epicor\'s job costing, MES, and shop floor management.',
  'Distribution process gaps from consultants without hands-on Prophet 21 or Eclipse expertise.',
  'Epicor upgrade delays from teams lacking experience with ERP 10 to Kinetic cloud migration methodology.',
  'Lost productivity from poorly trained end-users and misconfigured workflows that never get corrected.',
];

const solutionPoints = [
  'Epicor-specialist recruiters trained on the full Epicor product family',
  'Verified product-specific implementation experience and industry knowledge',
  'Right expertise matched to your product, version, and business environment',
];

const outcomes = [
  {
    title: 'Successful Epicor Go-Lives',
    text: 'Certified professionals who deliver manufacturing and distribution ERP implementations on time and on budget.',
  },
  {
    title: 'Optimized Manufacturing Operations',
    text: 'Consultants who configure job costing, shop floor, and MES to your exact production environment.',
  },
  {
    title: 'Smooth Kinetic Cloud Migrations',
    text: 'Specialists who manage ERP 10 to Kinetic transitions with minimal business disruption.',
  },
  {
    title: 'Higher ERP Adoption Rates',
    text: 'Experienced trainers and configurators who drive real user engagement and process compliance.',
  },
  {
    title: 'Scalable Epicor Talent Access',
    text: 'A pre-vetted bench of Epicor professionals ready for your next implementation or upgrade cycle.',
  },
];

const EpicorStaffHome = () => {
  return (
    <div>
      <Helmet>
        <title>Epicor Staffing India | Epicor Kinetic & ERP Recruitment | Kapil Technologies</title>
        <meta
          name="description"
          content="Kapil Technologies provides certified Epicor staffing solutions. Hire pre-vetted Epicor Kinetic consultants, Prophet 21 specialists, Eclipse experts, and Epicor technical developers for your ERP programs."
        />
        <meta
          name="keywords"
          content="Epicor staffing India, Epicor Kinetic recruitment, Epicor consultants India, Epicor ERP 10 staffing, Prophet 21 specialists, Epicor Eclipse experts, Epicor manufacturing consultants, Epicor BPM BAQ developers, Epicor Cloud migration, Epicor MES staffing, Epicor talent acquisition, Kapil Technologies Epicor"
        />
        <meta name="author" content="Kapil Technologies" />
        <meta name="robots" content="index, follow" />
        <meta
          property="og:title"
          content="Epicor Staffing India | Epicor Kinetic & ERP Recruitment | Kapil Technologies"
        />
        <meta
          property="og:description"
          content="Kapil Technologies provides certified Epicor staffing solutions. Hire pre-vetted Epicor Kinetic consultants and ERP specialists."
        />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_IN" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Epicor Staffing India | Epicor Kinetic & ERP Recruitment"
        />
        <meta
          name="twitter:description"
          content="Certified Epicor staffing solutions. Hire pre-vetted Epicor Kinetic consultants and ERP specialists."
        />
        <link
          rel="canonical"
          href="https://kapiltechnologies.com/staff-augmentation/epicor"
        />
      </Helmet>

      {/* Section 1: Hero */}
      <TechIntro 
        videoUrl="https://res.cloudinary.com/djaejg3nn/video/upload/v1781602800/Kapil_Tech_Epicor_Staffing_Page_pl363i.mp4"
        titleWhite="Certified Epicor Kinetic, Prophet 21 & ERP 10 Consultants."
        titleGradient="Epicor"
      />

      {/* Section 2: Why Choose */}
      <TechWhyChoose 
        title="Searching for Experienced Epicor ERP Consultants?"
        description="Kapil Tech connects manufacturers, distributors, and service organizations with pre-vetted Epicor professionals across Epicor Kinetic (ERP 10), Prophet 21, Eclipse, and BisTrack. Epicor expertise is rare — our specialist practice makes finding it fast and reliable."
        stats={[
          { value: "130+", label: "Epicor Experts Placed" },
          { value: "96%", label: "Client Satisfaction Rate" }
        ]}
        imageUrl="https://ik.imagekit.io/7wqj9br0b/KapilTech_Website_Staffing/Technology/Kapil_Tech_Epicor_Staffing_Page.jpg?updatedAt=1781603014076"
        imageAlt="Epicor Staffing by Kapil Tech"
      />

      {/* Section 3: Roles & Specializations */}
      <TechRoles 
        title="Epicor Products & Roles We Staff"
        description="Our Epicor talent network covers every major product, module, and technical specialization."
        roleGroups={roleGroups}
      />

      {/* Section 4: Why Enterprises Choose Us */}
      <TechStaffingServices 
        title="Why Manufacturers and Distributors Choose Us for Epicor Staffing"
        description="We combine deep Epicor domain expertise with an agile staffing model to deliver top-tier talent at speed."
        features={features}
      />

      {/* Section 5: Recruiting Agency / Problem-Solution */}
      <TechRecruitingAgency 
        title="Is the Epicor Talent Shortage Delaying Your ERP Delivery?"
        description="Epicor is a mid-market ERP leader with a loyal but limited certified consultant community. Generic staffing agencies routinely confuse Epicor product versions, misrepresent functional expertise, and deliver candidates who cannot navigate Epicor's manufacturing or distribution-specific configurations — resulting in costly project delays."
        problemTitle="The Real Cost of a Wrong Epicor hire"
        problemItems={problemItems}
        solutionTitle="Kapil Tech: Your Dedicated Epicor Staffing Partner"
        solutionDescription="Kapil Tech maintains an Epicor-specialist staffing practice with recruiters trained on the Epicor product family — Kinetic (ERP 10), Prophet 21, Eclipse, and BisTrack. We verify product-specific implementation experience and industry knowledge before any candidate reaches your hiring team."
        solutionPoints={solutionPoints}
        ctaText="Find Your Epicor Talent Today"
      />

      {/* Section 6: Business Outcomes */}
      <TechOutcomes 
        title="Business Outcomes You Can Expect"
        description="Partner with Kapil Tech to accelerate your Epicor initiatives and achieve measurable business results through our expert talent solutions."
        outcomes={outcomes}
      />

      {/* Section 7: CTA */}
      <TechCTA 
        title="Ready to Hire Top"
        titleGradient="Epicor Experts?"
        description="Share your Epicor staffing requirements today. Our team responds within hours with pre-vetted Epicor professionals matched to your product, version, and industry."
      />
    </div>
  );
};

export default EpicorStaffHome;
